import React, { useState, useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Caption1Strong,
  Caption1,
  Button,
  Label,
  Switch,
  ProgressBar,
  mergeClasses
}
  from '@fluentui/react-components';
import { MessageBar, MessageBarTitle, MessageBarBody } from '@fluentui/react-message-bar';
import { ArrowClockwise20Regular, Dismiss20Regular, Sparkle20Regular } from '@fluentui/react-icons';
import { CopilotIcon } from '@bingads-webui-react/copilot-ui-base';
import { StatusEnum } from '@bingads-webui-campaign-react/copilot-domain-data-service';
import { PreviewClipchampVideoModal } from '@bingads-webui-campaign-react/clipchamp-player';
import { useImageAnimationStyles } from './style';
import { maxVideoCount } from '../../util';

// TODO: move this to another file?
const mapGenerateVideoClipsRequest = metadata => (
  [
    {
      ImageUrl: metadata.imageUrl,
      GenerationType: 'ImageToVideo',
      Height: metadata.height,
      Width: metadata.width,
      MaxCountVideo: maxVideoCount,
      FinalURL: metadata.finalUrl,
      Texts: metadata.texts,
      UserPrompt: null, // for future implementations
    },
  ]
);

export const TryAnimationPrompt = ({
  i18n,
  handleAnimateImage,
}) => {
  const handleTryIt = useCallback(() => {
    const traceFunction = 'TryAnimationPrompt/handleAnimateImage';
    const traceMessage = 'User clicked "Try it" button';
    handleAnimateImage(traceFunction, traceMessage);
  }, [handleAnimateImage]);

  const classes = useImageAnimationStyles();

  return (
    <div className={mergeClasses(classes.animationContainer, classes.tryAnimationPromptContainer)}>
      <div className={classes.tryAnimationPromptTitle}>
        <CopilotIcon
          size="small"
          color="multicolor"
        />
        <div className={classes.tryAnimationPromptText}>
          <Caption1Strong>{i18n.getString(_TL_('Animate Your Image'))}</Caption1Strong>
          <Caption1>{i18n.getString(_TL_('AI will turn your image into a video'))}</Caption1>
        </div>
      </div>
      <div className={classes.tryAnimationPromptButton}>
        <Button
          icon={<Sparkle20Regular />}
          onClick={handleTryIt}
          className="try-it-button"
          aria-label={i18n.getString(_TL_('Animate Your Image Now'))}
        >
          {i18n.getString(_TL_('Try it'))}
        </Button>
      </div>
    </div>
  );
};

TryAnimationPrompt.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  handleAnimateImage: PropTypes.func.isRequired,
};

export const AnimationPending = ({
  i18n,
  cancelTask,
  clipId,
}) => {
  const classes = useImageAnimationStyles();
  const onCancel = () => {
    cancelTask(clipId);
  };

  return (
    <div className={classes.animationContainer}>
      <Label size="large">{i18n.getString(_TL_('Animation'))}</Label>
      <ProgressBar />
      <MessageBar>
        <MessageBarBody>
          <MessageBarTitle>{i18n.getString(_TL_('Processing asset'))}</MessageBarTitle>
          {i18n.getString(_TL_('We are processing your image into a video asset. This can take a few minutes, and this preview will change once complete.'))}
        </MessageBarBody>
      </MessageBar>
      <div className={classes.actionButton}>
        <Button appearance="transparent" icon={<Dismiss20Regular />} onClick={onCancel}>
          {i18n.getString(_TL_('Cancel'))}
        </Button>
      </div>
    </div>
  );
};

AnimationPending.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  cancelTask: PropTypes.func.isRequired,
};

export const EnableAnimationToggle = ({
  i18n,
  clipUrl,
  logger,
  onAnimationToggled,
  animationToggleState,
  taskId,
  handleAnimateImage,
}) => {
  const classes = useImageAnimationStyles();
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [isChecked, setChecked] = useState(false);

  const onClosePreviewModal = useCallback(() => {
    setShowPreviewModal(false);
  }, [setShowPreviewModal]);

  const handleModalOpen = useCallback(() => {
    setShowPreviewModal(true);
  }, [setShowPreviewModal]);

  const onHandleAnimationToggle = useCallback((e) => {
    setChecked(e.currentTarget.checked);
    onAnimationToggled(taskId, e.currentTarget.checked);
  }, [taskId, onAnimationToggled, setChecked]);

  const onRetry = useCallback(() => {
    const traceFunction = 'EnableAnimationToggle/handleAnimateImage';
    const traceMessage = 'User clicked "Try again" button';
    handleAnimateImage(traceFunction, traceMessage);
  }, [handleAnimateImage]);

  useEffect(() => {
    setChecked(animationToggleState[taskId] !== false);
  }, [animationToggleState, taskId, setChecked]);


  return (
    <div className={mergeClasses(classes.animationContainer, 'completed-clip-container')}>
      {showPreviewModal && <PreviewClipchampVideoModal
        i18n={i18n}
        show={showPreviewModal}
        videoUrl={clipUrl}
        onClose={onClosePreviewModal}
        logger={logger}
        onShowChange={setShowPreviewModal}
      />}
      <div className={classes.animationToggleTitle}>
        <Label size="medium">{i18n.getString(_TL_('Animation'))}</Label>
        <Switch
          checked={isChecked}
          onChange={onHandleAnimationToggle}
          aria-label={i18n.getString(_TL_('Toggle animation'))}
        />
      </div>
      <div className={classes.animationPreview}>
        <video
          className={classes.animationPreviewVideo}
          muted
          loop
          onMouseEnter={e => e.currentTarget.play()}
          onMouseLeave={(e) => {
            e.currentTarget.pause();
            e.currentTarget.currentTime = 0;
           }
         }
          onClick={() => handleModalOpen(clipUrl)}
          aria-label={i18n.getString(_TL_('Animation preview'))}
        >
          <track kind="captions" />
          <source src={clipUrl} type="video/mp4" />
          {i18n.getString(_TL_('Your browser does not support video.'))}
        </video>
      </div>
      <div className={classes.actionButton}>
        <Button
          appearance="transparent"
          icon={<ArrowClockwise20Regular />}
          onClick={onRetry}
          className={classes.actionButton}
          aria-label={i18n.getString(_TL_('Try again'))}
        >
          {i18n.getString(_TL_('Try again'))}
        </Button>
      </div>
    </div>
  );
};

EnableAnimationToggle.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  clipUrl: PropTypes.string.isRequired,
  logger: PropTypes.shape({
    logError: PropTypes.func,
    logTrace: PropTypes.func,
  }).isRequired,
  onAnimationToggled: PropTypes.func.isRequired,
  animationToggleState: PropTypes.shape({}).isRequired,
  taskId: PropTypes.string.isRequired,
  handleAnimateImage: PropTypes.func.isRequired,
};

export const ImageAnimationWrapper = ({
  i18n,
  copilotDataService,
  logger,
  activity,
  template,
  imageUrl,
  finalUrl,
  scene,
  addTask,
  cancelTask,
  selectedSceneId,
  sceneImageId,
  imageIndex,
  clip,
  onAnimationToggled,
  animationToggleState,
}) => {
  const { height, width } = template || {};
  const { text } = scene || {};

  const mock = true; // TODO: Remove mock when backend is ready

  const metadata = {
    height,
    width,
    imageUrl,
    texts: text,
    finalUrl,
  };

  const handleAnimateImage = (traceFunction, traceMessage) => {
    activity.trace(traceFunction, traceMessage);
    try {
      copilotDataService.generateVideoClips(mapGenerateVideoClipsRequest(metadata), mock).then((response) => {
        const { VideoClipTasksInfo } = response.Data || {};
        if (VideoClipTasksInfo && VideoClipTasksInfo.length > 0) {
          const task = VideoClipTasksInfo[0];
          const { TaskId } = task || {};
          if (TaskId) {
            addTask(TaskId, selectedSceneId, imageUrl, imageIndex);
          }
        }
      });
    } catch (error) {
      logger.logError('Failed to generate video clips', error);
    }
  };

  const status = clip.Status || StatusEnum.NOT_STARTED;
  const clipUrl = (clip.RecommendedVideoClips && clip.RecommendedVideoClips[0] && clip.RecommendedVideoClips[0].Url) || null;

  const showTryAnimationPrompt = useMemo(() => status === StatusEnum.NOT_STARTED, [status]);

  const showAnimationPending = useMemo(() => status === StatusEnum.PENDING, [status]);

  const showCompletedAnimation = useMemo(() => status === StatusEnum.COMPLETED, [status]);

  return (
    <div className="image-animation-wrapper">
      { showTryAnimationPrompt && (
        <TryAnimationPrompt
          i18n={i18n}
          copilotDataService={copilotDataService}
          logger={logger}
          activity={activity}
          template={template}
          imageUrl={imageUrl}
          imageIndex={imageIndex}
          finalUrl={finalUrl}
          scene={scene}
          selectedSceneId={selectedSceneId}
          addTask={addTask}
          sceneImageId={sceneImageId}
          handleAnimateImage={handleAnimateImage}
        />)}
      { showAnimationPending && (
        <AnimationPending
          i18n={i18n}
          cancelTask={cancelTask}
          clipId={clip.id}
        />
    )}
      { showCompletedAnimation && clipUrl && (
      <EnableAnimationToggle
        i18n={i18n}
        clipUrl={clipUrl}
        logger={logger}
        sceneImageId={sceneImageId}
        selectedSceneId={selectedSceneId}
        imageUrl={imageUrl}
        onAnimationToggled={onAnimationToggled}
        animationToggleState={animationToggleState}
        taskId={clip.id}
        handleAnimateImage={handleAnimateImage}
      />
    )}
    </div>
  );
};

ImageAnimationWrapper.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  copilotDataService: PropTypes.shape({
    generateVideoClips: PropTypes.func,
  }).isRequired,
  logger: PropTypes.shape({
    logError: PropTypes.func,
  }).isRequired,
  activity: PropTypes.shape({
    trace: PropTypes.func,
  }).isRequired,
  template: PropTypes.shape({
    height: PropTypes.number,
    width: PropTypes.number,
  }).isRequired,
  imageUrl: PropTypes.string.isRequired,
  imageIndex: PropTypes.number.isRequired,
  finalUrl: PropTypes.string.isRequired,
  scene: PropTypes.shape({
    text: PropTypes.string,
  }).isRequired,
  addTask: PropTypes.func.isRequired,
  cancelTask: PropTypes.func.isRequired,
  sceneImageId: PropTypes.string.isRequired,
  selectedSceneId: PropTypes.string.isRequired,
  clip: PropTypes.shape({
    Status: PropTypes.string,
    RecommendedVideoClips: PropTypes.arrayOf(PropTypes.shape({
      Url: PropTypes.string,
    })),
  }).isRequired,
  onAnimationToggled: PropTypes.func.isRequired,
  animationToggleState: PropTypes.shape({}).isRequired,
};

export const AnimationUnavailable = ({
  i18n,
}) => {
  const classes = useImageAnimationStyles();

  return (
    <div className={classes.animationContainer}>
      <MessageBar>
        <MessageBarBody>
          <MessageBarTitle>{i18n.getString(_TL_('Limited functionality notification'))}</MessageBarTitle>
          {i18n.getString(_TL_('For the ability to add animation to this image please go to Ads Studio.'))}
        </MessageBarBody>
      </MessageBar>
    </div>
  );
};

AnimationUnavailable.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
};
